import * as React from 'react';
import {
  Cell as AriaCell,
  Collection as AriaCollection,
  Column as AriaColumn,
  Group as AriaGroup,
  Row as AriaRow,
  Table as AriaTable,
  TableBody as AriaTableBody,
  TableHeader as AriaTableHeader,
} from 'react-aria-components';

import { ArrowDownIcon, ChevronSelectorIcon, CopyIcon, EditIcon, HelpCircleIcon, TrashIcon } from '../../icons';
import { cx } from '../../utils/cx';
import Badge from '../Badge/Badge';
import Dropdown from '../Dropdown';
import Tooltip, { TooltipTrigger } from '../Tooltip';

import type {
  CellProps as AriaCellProps,
  ColumnProps as AriaColumnProps,
  RowProps as AriaRowProps,
  TableBodyProps as AriaTableBodyProps,
  TableHeaderProps as AriaTableHeaderProps,
  TableProps as AriaTableProps,
} from 'react-aria-components';

export const TableRowActionsDropdown = () => (
  <Dropdown.Root>
    <Dropdown.DotsButton />

    <Dropdown.Popover className="w-min">
      <Dropdown.Menu>
        <Dropdown.Item icon={EditIcon}>
          <span className="pr-4">Edit</span>
        </Dropdown.Item>
        <Dropdown.Item icon={CopyIcon}>
          <span className="pr-4">Copy link</span>
        </Dropdown.Item>
        <Dropdown.Item icon={TrashIcon}>
          <span className="pr-4">Delete</span>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown.Popover>
  </Dropdown.Root>
);

const TableContext = React.createContext<{ size: 'sm' | 'md' }>({ size: 'md' });

const TableCardRoot = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { size?: 'sm' | 'md' }>(
  ({ children, className, size = 'md', ...props }, ref) => {
    return (
      <TableContext.Provider value={{ size }}>
        <div
          ref={ref}
          {...props}
          className={cx(
            'bg-secondary_subtle border-secondary shadow-card overflow-hidden rounded-2xl border',
            className
          )}
        >
          {children}
        </div>
      </TableContext.Provider>
    );
  }
);
TableCardRoot.displayName = 'TableCardRoot';

interface ITableCardHeaderProps {
  /** The title of the table card header. */
  title: string;
  /** The subtitle displayed directly under the title. */
  subtitle?: React.ReactNode;
  /** The badge displayed next to the title. */
  badge?: React.ReactNode;
  /** The description of the table card header. */
  description?: string;
  /** The content displayed after the title and badge. */
  contentTrailing?: React.ReactNode;
  /** When true, title and trailing are always one row, left-aligned. */
  trailingInline?: boolean;
  /** The class name of the table card header. */
  className?: string;
}

const TableCardHeader = ({
  title,
  subtitle,
  badge,
  description,
  contentTrailing,
  trailingInline,
  className,
}: ITableCardHeaderProps) => {
  const { size } = React.useContext(TableContext);

  return (
    <div
      className={cx(
        'border-tertiary bg-secondary relative flex border-b px-4 md:px-5',
        size === 'sm' ? 'py-4' : 'py-5',
        trailingInline ? 'flex-row items-center gap-4' : 'flex-col items-center gap-4 md:flex-row',
        className
      )}
    >
      <div className="flex min-w-0 max-w-full flex-1 flex-col">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex min-w-0 flex-col">
            <h2 className="text-primary truncate text-lg font-semibold">{title}</h2>
            {subtitle && typeof subtitle === 'string' ? (
              <span className="text-tertiary truncate text-sm font-normal">{subtitle}</span>
            ) : (
              subtitle
            )}
          </div>
          {badge ? (
            React.isValidElement(badge) ? (
              badge
            ) : (
              <Badge color="brand" size="sm">
                {badge}
              </Badge>
            )
          ) : null}
        </div>
        {description && <p className="text-tertiary text-sm">{description}</p>}
      </div>
      {contentTrailing}
    </div>
  );
};

interface ITableRootProps
  extends AriaTableProps, Omit<React.ComponentPropsWithRef<'table'>, 'className' | 'slot' | 'style'> {
  size?: 'sm' | 'md';
}

const TableRoot = ({ className, size = 'md', ...props }: ITableRootProps) => {
  const context = React.useContext(TableContext);

  return (
    <TableContext.Provider value={{ size: context?.size ?? size }}>
      <div className="overflow-x-auto">
        <AriaTable
          className={(state) =>
            cx('w-full overflow-x-hidden', typeof className === 'function' ? className(state) : className)
          }
          selectionMode={props.selectionMode ?? 'single'}
          {...props}
        />
      </div>
    </TableContext.Provider>
  );
};
TableRoot.displayName = 'Table';

interface ITableHeaderProps<T extends object>
  extends
    AriaTableHeaderProps<T>,
    Omit<React.ComponentPropsWithRef<'thead'>, 'children' | 'className' | 'slot' | 'style'> {
  bordered?: boolean;
}

const TableHeader = <T extends object>({
  columns,
  children,
  bordered = true,
  className,
  ...props
}: ITableHeaderProps<T>) => {
  const { size } = React.useContext(TableContext);

  return (
    <AriaTableHeader
      {...props}
      className={(state) =>
        cx(
          'bg-primary relative',
          size === 'sm' ? 'h-9' : 'h-11',

          // Row border—using an "after" pseudo-element to avoid the border taking up space.
          bordered &&
            '[&>tr>th]:after:bg-border-tertiary [&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:focus-visible:after:bg-transparent',

          typeof className === 'function' ? className(state) : className
        )
      }
    >
      <AriaCollection items={columns}>{children}</AriaCollection>
    </AriaTableHeader>
  );
};

TableHeader.displayName = 'TableHeader';

const TableBody = <T extends object>({ className, ...props }: AriaTableBodyProps<T>) => {
  return (
    <AriaTableBody
      {...props}
      className={(state) => cx('bg-primary', typeof className === 'function' ? className(state) : className)}
    />
  );
};
TableBody.displayName = 'TableBody';

interface ITableHeadProps
  extends
    AriaColumnProps,
    Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'children' | 'className' | 'style' | 'id'> {
  label?: string;
  tooltip?: string;
}

const TableHead = ({ className, tooltip, label, children, ...props }: ITableHeadProps) => {
  return (
    <AriaColumn
      {...props}
      className={(state) =>
        cx(
          'outline-hidden focus-visible:z-1 focus-visible:ring-focus-ring focus-visible:ring-offset-bg-primary relative p-0 px-6 py-3 focus-visible:ring-2 focus-visible:ring-inset',
          state.allowsSorting && 'cursor-pointer',
          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {(state) => (
        <AriaGroup className="flex items-center gap-1">
          <div className="flex items-center gap-1">
            {label && <span className="text-quaternary whitespace-nowrap text-sm font-semibold">{label}</span>}
            {typeof children === 'function' ? children(state) : children}
          </div>

          {tooltip && (
            <Tooltip title={tooltip} placement="top">
              <TooltipTrigger className="text-fg-quaternary hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover cursor-pointer transition duration-100 ease-linear">
                <HelpCircleIcon className="size-4" />
              </TooltipTrigger>
            </Tooltip>
          )}

          {state.allowsSorting &&
            (state.sortDirection ? (
              <ArrowDownIcon
                className={cx(
                  'text-fg-quaternary size-3 stroke-[3px]',
                  state.sortDirection === 'ascending' && 'rotate-180'
                )}
              />
            ) : (
              <ChevronSelectorIcon size={12} strokeWidth={3} className="text-fg-quaternary" />
            ))}
        </AriaGroup>
      )}
    </AriaColumn>
  );
};
TableHead.displayName = 'TableHead';

interface ITableRowProps<T extends object>
  extends
    AriaRowProps<T>,
    Omit<React.ComponentPropsWithRef<'tr'>, 'children' | 'className' | 'onClick' | 'slot' | 'style' | 'id'> {
  highlightSelectedRow?: boolean;
}

const TableRow = <T extends object>({
  columns,
  children,
  className,
  highlightSelectedRow = false,
  ...props
}: ITableRowProps<T>) => {
  const { size } = React.useContext(TableContext);

  return (
    <AriaRow
      {...props}
      className={(state) =>
        cx(
          'hover:bg-utility-brand-25 outline-focus-ring relative transition-colors after:pointer-events-none focus-visible:outline-2 focus-visible:-outline-offset-2',
          size === 'sm' ? 'h-14' : 'h-18',
          highlightSelectedRow && 'selected:bg-secondary',

          // Row border—using an "after" pseudo-element to avoid the border taking up space.
          '[&>td]:after:bg-border-tertiary [&>td]:after:absolute [&>td]:after:inset-x-0 [&>td]:after:bottom-0 [&>td]:after:h-px [&>td]:after:w-full last:[&>td]:after:hidden [&>td]:focus-visible:after:opacity-0',

          typeof className === 'function' ? className(state) : className
        )
      }
    >
      <AriaCollection items={columns}>{children}</AriaCollection>
    </AriaRow>
  );
};

TableRow.displayName = 'TableRow';

interface ITableCellProps
  extends AriaCellProps, Omit<React.TdHTMLAttributes<HTMLTableCellElement>, 'children' | 'className' | 'style' | 'id'> {
  ref?: React.Ref<HTMLTableCellElement>;
}

const TableCell = ({ className, children, ...props }: ITableCellProps) => {
  const { size } = React.useContext(TableContext);

  return (
    <AriaCell
      {...props}
      className={(state) =>
        cx(
          'text-tertiary outline-focus-ring focus-visible:z-1 relative text-sm focus-visible:outline-2 focus-visible:-outline-offset-2',
          size === 'sm' && 'px-5 py-3',
          size === 'md' && 'px-6 py-4',

          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {children}
    </AriaCell>
  );
};
TableCell.displayName = 'TableCell';

export const TableCard = {
  Root: TableCardRoot,
  Header: TableCardHeader,
};

const Table = TableRoot as typeof TableRoot & {
  Body: typeof TableBody;
  Cell: typeof TableCell;
  Head: typeof TableHead;
  Header: typeof TableHeader;
  Row: typeof TableRow;
};
Table.Body = TableBody;
Table.Cell = TableCell;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;

export default Table;
